<template>
    <div
        class="patsnap-biz-workspace_folder_tree"
        :style="`width: ${service.treeWidth};borderRadius: ${service.borderRadius}`"
    >
        <div
            v-if="nodata"
            class="workspace_folder_tree__nodata"
        >
            <PtIcon
                class="workspace_folder_tree__nodata-icon"
                icon="SolidNoDataTip"
            />
            <p class="workspace_folder_tree__nodata-tips">
                {{ $t('PtWorkspaceFolderTree.noData') }}
            </p>
        </div>
        <PtIcon
            v-else-if="loadingWorkspaces"
            class="workspace_folder_tree__loading"
            icon="SolidLoadingBars"
        />
        <template v-else>
            <div class="workspace_folder_tree__left">
                <ElCollapse :value="collapseActiveName">
                    <ElCollapseItem
                        v-for="{ title, type, workspaces } in allWorkspaces"
                        :key="type"
                        :name="type"
                        :disabled="!service.allowCollapse"
                    >
                        <template slot="title">
                            <span>{{ title }}</span>
                        </template>

                        <ul
                            class="workspace_folder_tree__workspace"
                        >
                            <li
                                v-for="workspace in workspaces"
                                :key="workspace.space_id"
                                :class="{
                                    active: currentWorkspaceId === workspace.space_id,
                                    'workspace_folder_tree__workspace-item--selected': selectedWsIdList.includes(workspace.space_id)
                                }"
                                class="workspace_folder_tree__workspace-item"
                                @click="handleClickWorkspace(workspace)"
                            >
                                <PtIcon
                                    class="workspace_folder_tree__workspace-item__tick"
                                    icon="SolidTick"
                                />

                                <PtIcon
                                    class="workspace_folder_tree__workspace-item__icon"
                                    icon="Solid3D"
                                />
                                <ElTooltip
                                    :placement="'top-start'"
                                    :open-delay="300"
                                    :visible-arrow="false"
                                    :enterable="false"
                                    offset="-30"
                                    popper-class="workspace-folder-tree-tooltip"
                                    :content="service.isShowPatentCount ? `${workspace.space_name} (${kilo(workspace.total_patent_count)})` : workspace.space_name"
                                    effect="light"
                                >
                                    <div class="workspace_folder_tree__workspace-item__info">
                                        <PtTextTruncate
                                            tag="span"
                                            :lines="1"
                                        >
                                            {{ workspace.space_name }}
                                        </PtTextTruncate>
                                        <span
                                            v-if="service.isShowPatentCount"
                                            class="workspace_folder_tree__workspace-item__info-count"
                                        >({{ kilo(workspace.total_patent_count) }})</span>
                                    </div>
                                </ElTooltip>
                            </li>
                        </ul>
                    </ElCollapseItem>
                </ElCollapse>
            </div>
            <div class="workspace_folder_tree__right">
                <PtIcon
                    v-if="loadingFolders"
                    class="workspace_folder_tree__loading workspace_folder_tree"
                    icon="SolidLoadingBars"
                    style="fontSize: 32px;"
                />
                <PtVirtualTree
                    v-else
                    ref="tree"
                    node-key="id"
                    :default-expand-all="service.defaultExpandAll"
                    :default-expand-keys="expendFolderIdList"
                    item-class="workspace_folder_tree__node"
                    :check-strictly="service.checkStrictly"
                    :data="folderTree"
                    :item-size="32"
                >
                    <template #empty>
                        <div class="workspace_folder_tree__node-nodata">
                            <span>{{ $t('PtWorkspaceFolderTree.noFolder') }}</span>
                        </div>
                    </template>
                    <template
                        #default="{
                            node,
                            data,
                            isExpand,
                            isChecked,
                            isShowExpandIcon,
                            toggleNodeExpand,
                            toggleCheck,
                        }"
                    >
                        <div

                            class="workspace_folder_tree__item"
                            :style="{ paddingLeft: (node.level - 1) * 20 + 'px'}"
                        >
                            <div
                                v-if="service.multipleFolder"
                                class="workspace_folder_tree__item__checkbox"
                            >
                                <PtIcon
                                    class="workspace_folder_tree__item__checkbox-icon"
                                    :icon="isChecked ? 'SolidCheckboxOnNew' : 'SolidCheckboxOffNew'"
                                    @click="onSelectFolder({ node, toggleCheck, isChecked })"
                                />
                            </div>
                            <div class="workspace_folder_tree__item__icon">
                                <PtIcon
                                    v-if="isShowExpandIcon"
                                    class="item__icon parent-item"
                                    :icon="isExpand ? 'SolidDropdownOpen' : 'SolidDropdownRight'"
                                    @click="toggleNodeExpand(node)"
                                />
                                <PtIcon
                                    v-else
                                    class="item__icon child-item"
                                    icon="SolidSmallDot"
                                />
                            </div>

                            <ElTooltip
                                :placement="'top-start'"
                                :open-delay="300"
                                :visible-arrow="false"
                                :enterable="false"
                                offset="-30"
                                popper-class="workspace-folder-tree-tooltip"
                                :content="service.isShowPatentCount ? `${data.folder_name} (${kilo(data.patent_count)})` : data.folder_name"
                                effect="light"
                            >
                                <div
                                    class="workspace_folder_tree__item__content"
                                    @click="onSelectFolder({ node, toggleCheck, isChecked })"
                                >
                                    <PtIcon
                                        class="workspace_folder_tree__item__content-icon"
                                        :icon="data.folder_auto_update ? 'SolidAutoFolder' : 'SolidFolderClosed2'"
                                    />
                                    <PtTextTruncate
                                        tag="span"
                                        :lines="1"
                                    >
                                        <span v-html="getHighlightName(data.folder_name)" />
                                    </PtTextTruncate>
                                    <span
                                        v-if="service.isShowPatentCount"
                                        class="workspace_folder_tree__item__content-count"
                                    >({{ kilo(data.patent_count) }})</span>
                                </div>
                            </ElTooltip>
                        </div>
                    </template>
                </PtVirtualTree>
            </div>
        </template>
    </div>
</template>

<script>
/* eslint-disable func-names */

import Collapse from 'element-ui/lib/collapse';
import CollapseItem from 'element-ui/lib/collapse-item';
import Tooltip from 'element-ui/lib/tooltip';
import PtTextTruncate from '@patsnap-biz/text-truncate';
import PtVirtualTree from '@patsnap-biz/virtual-tree';

import _ from 'lodash';
import PtIcon from '@patsnap-biz/icon';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';
import '@patsnap-ui/icon/assets/solid/3d.svg';
import '@patsnap-ui/icon/assets/solid/SmallDot.svg';
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/DropdownRight.svg';
import '@patsnap-ui/icon/assets/solid/autoFolder.svg';
import '@patsnap-ui/icon/assets/solid/FolderClosed2.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import '@patsnap-ui/icon/assets/solid/CheckboxOffNew.svg';
import '@patsnap-ui/icon/assets/solid/CheckboxOnNew.svg';
import '@patsnap-ui/icon/assets/solid/tick.svg';

import cn from '../locales/cn.js';
import en from '../locales/en.js';
import jp from '../locales/jp.js';
import tw from '../locales/tw.js';

export default {
    name: 'PtWorkspaceFolderTree',
    components: {
        PtIcon,
        PtTextTruncate,
        PtVirtualTree,
        ElCollapse: Collapse,
        ElCollapseItem: CollapseItem,
        ElTooltip: Tooltip,
    },
    i18n: {
        fallbackLocale: 'cn',
        messages: {
            cn, en, jp, tw,
        },
    },

    props: {
        /**
         * 继承 WorkspaceFolderTreeService 的类的实例
         */
        service: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            nodata: false,
            loadingWorkspaces: true,
            collapseActiveName: 'self',
            currentWorkspace: undefined,
            currentWorkspaceId: '',
            loadingFolders: false,
            folderTree: [],

            expendFolderIdList: [],
        };
    },

    computed: {
        selectedWsIdList() {
            return _.uniq(_.map(this.service.selectedFolderList, (item) => item.space_id));
        },
        allWorkspaces() {
            const list = [];
            if (this.service.workspacesData) {
                if (this.service.myWorkspaces.length > 0) {
                    list.push({
                        type: 'self',
                        title: this.$t('PtWorkspaceFolderTree.myWorkspace'),
                        workspaces: this.service.myWorkspaces,
                    });
                }
                if (this.service.sharedWorkspaces.length > 0) {
                    list.push({
                        type: 'shared',
                        title: this.$t('PtWorkspaceFolderTree.sharedWorkspace'),
                        workspaces: this.service.sharedWorkspaces,
                    });
                }
                if (this.service.publicWorkspaces.length > 0) {
                    list.push({
                        type: 'public',
                        title: this.$t('PtWorkspaceFolderTree.publicWorkspace'),
                        workspaces: this.service.publicWorkspaces,
                    });
                }
            }
            return list;
        },
        currentPatentsCounts() {
            return _.reduce(this.service.selectedFolderList, (count, folder) => folder.patent_count + count, 0);
        },
    },

    watch: {
        'service.keyword': {
            handler: _.debounce(function () {
                /* 加载 workspaces */
                this.loadWorkspaces();
            }, 100),
            // immediate: true,
        },

        'service.selectedFolderList': {
            handler() {
                this.setDefaultFolderChecked();
            },
        },
    },

    created() {
        this.loadWorkspaces();
    },

    methods: {
        kilo(num = 0) {
            if (Number.isNaN(num)) {
                return num;
            }
            const str = new Intl.NumberFormat('en-US', { style: 'decimal' }).format(num);
            return `${str}`;
        },

        getExpendFolders(list) {
            _.forEach(list, (folder) => {
                if (folder.children && folder.children.length > 0) {
                    const isNotMatchSubFolder = _.get(folder.children, '0.not_match_sub_folder', false);
                    if (!isNotMatchSubFolder) {
                        this.expendFolderIdList.push(folder.folder_id);
                        this.getExpendFolders(folder.children);
                    }
                }
            });
        },
        async loadWorkspaces() {
            this.loadingWorkspaces = true;
            this.nodata = false;
            await this.service.getWorkspacesData().finally(() => {
                this.loadingWorkspaces = false;
            });
            if (!this.service.myWorkspaces.length && !this.service.sharedWorkspaces.length && !this.service.publicWorkspaces.length) {
                this.nodata = true;
            } else {
                if (!this.service.allowCollapse) {
                    this.collapseActiveName = this.allWorkspaces.map((item) => item.type);
                }

                if (this.allWorkspaces && this.allWorkspaces.length > 0) {
                    let isFind = false;
                    // 如果默认的workspaceId
                    if (this.currentWorkspaceId) {
                        for (let i = 0; i < this.allWorkspaces.length; i += 1) {
                            const workspace = this.allWorkspaces[i];
                            for (let j = 0; j < workspace.workspaces.length; j += 1) {
                                const ws = workspace.workspaces[j];
                                if (ws.space_id === this.currentWorkspaceId) {
                                    isFind = true;
                                    if (this.service.allowCollapse) {
                                        this.collapseActiveName = workspace.type;
                                    }
                                    // eslint-disable-next-line prefer-destructuring
                                    this.currentWorkspace = ws;
                                    this.currentWorkspaceId = ws.space_id;
                                    // 滚动到 选中的位置
                                    this.$nextTick(() => {
                                        const elList = document.querySelectorAll('.workspace_folder_tree__workspace-item');
                                        if (elList && elList[j]) {
                                            elList[j].scrollIntoView();
                                        }
                                    });
                                    break;
                                }
                            }
                            if (isFind) {
                                break;
                            }
                        }
                    }
                    // 如果没有找到
                    if (!isFind) {
                        if (this.service.allowCollapse) {
                            this.collapseActiveName = this.allWorkspaces[0].type;
                        }
                        // eslint-disable-next-line prefer-destructuring
                        this.currentWorkspace = this.allWorkspaces[0].workspaces[0];
                        this.currentWorkspaceId = this.currentWorkspace.space_id;
                    }
                    await this.loadFolders(this.currentWorkspaceId);
                }
            }
        },

        async loadFolders() {
            this.loadingFolders = true;

            const currentRequestWsId = this.currentWorkspaceId;
            const folderList = await this.service.getFoldersData(this.currentWorkspace);
            if (currentRequestWsId !== this.currentWorkspaceId) return;
            this.folderTree = this.service.folderListToTree(folderList);
            if (!this.service.defaultExpandAll) {
                this.expendFolderIdList = [];
                this.getExpendFolders(this.folderTree);
            }

            this.loadingFolders = false;

            if (this.service.multipleFolder) {
                // 将默认值填充到selected中
                this.setDefaultFolderIntoSelected();
                // 文件夹多选 默认选中
                this.setDefaultFolderChecked();
            }
        },
        setDefaultFolderChecked() {
            const selectedFolderList = [];
            _.forEach(this.service.folderList, (folder) => {
                if (_.find(this.service.selectedFolderList, { folder_id: folder.folder_id })) {
                    selectedFolderList.push(folder);
                }
            });

            this.$nextTick(() => {
                if (this.$refs.tree) {
                    this.$refs.tree.setCheckedKeys(selectedFolderList.map((item) => item.folder_id));
                }
            });
        },

        setDefaultFolderIntoSelected() {
            const includesFolderId = [];
            const selectedFolderList = [];
            _.forEach(this.service.folderList, (folder) => {
                const folderId = folder.folder_id;
                if (this.service.defaultFolderIdList.includes(folderId)) {
                    // 防止多次重复push
                    const isSelected = _.find(this.service.selectedFolderList, { folder_id: folderId });
                    if (!isSelected) {
                        selectedFolderList.push(folder);
                        includesFolderId.push(folderId);
                    }
                }
            });

            if (selectedFolderList.length > 0) {
                this.service.setSelectedFolderList(_.concat(this.service.selectedFolderList, selectedFolderList));
            }

            if (includesFolderId > 0) {
                this.service.setDefaultFolderIdList(_.xor(this.service.defaultFolderIdList, includesFolderId));
            }
        },

        async handleClickWorkspace(workspace) {
            this.currentWorkspace = workspace;
            this.currentWorkspaceId = workspace.space_id;

            await this.loadFolders(this.currentWorkspaceId);
        },

        getHighlightName(name = '') {
            if (!_.trim(this.service.keyword)) return name;
            const keyWordsReg = new RegExp(_.escapeRegExp(_.trim(this.service.keyword)), 'gi');
            const highlightName = name.replace(
                keyWordsReg,
                (match) => `<span class="folder-high-color">${match}</span>`,
            );
            return highlightName;
        },
        onSelectFolder({ node, toggleCheck, isChecked }) {
            const folder = _.cloneDeep(node.data);
            let selectedFolderList = _.cloneDeep(this.service.selectedFolderList);
            if (this.service.multipleFolder) {
                if (isChecked) {
                    const index = _.findIndex(selectedFolderList, { id: folder.id });
                    selectedFolderList.splice(index, 1);
                } else {
                    if ((this.service.maxPatentsCount && this.currentPatentsCounts + folder.patent_count > this.service.maxPatentsCount)
                    || (this.service.maxFoldersCount && selectedFolderList.length >= this.service.maxFoldersCount)) {
                        this.service.overLimitCallback();
                        return;
                    }
                    selectedFolderList.push(folder);
                }
                toggleCheck(node);
            } else {
                selectedFolderList = [folder];
            }
            this.service.onSelectedFolderChange(selectedFolderList);
        },
    },
};
</script>
